import { useCallback, useEffect, useState } from 'react';
import type { Program } from '@starter/shared';
import { useAuth, useProgramId } from '../../auth/AuthContext.js';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';

export function ProgramSettingsPage() {
  const programId = useProgramId();
  const { user } = useAuth();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [name, setName] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .getProgram(programId)
      .then((p) => {
        setProgram(p);
        setName(p.name);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load program'))
      .finally(() => setLoading(false));
  }, [programId]);

  useEffect(load, [load]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const updated = await api.updateProgram(programId, { name });
      setProgram(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingState message="Loading program settings…" />;
  if (error) return <ErrorBanner message={error} onRetry={load} />;
  if (!program) return null;

  return (
    <div className="page">
      <PageHeader title="Program Settings" subtitle="Basic program configuration" />

      <div className="card">
        <dl className="detail-list detail-list--inline">
          <dt>Organization</dt>
          <dd>{user?.authKind === 'tenant' ? (user.organizationName ?? '—') : '—'}</dd>
          <dt>Program slug</dt>
          <dd>
            <code>{program.slug}</code>
          </dd>
          <dt>Status</dt>
          <dd>
            <StatusBadge status={program.status} variant={program.status === 'active' ? 'success' : 'muted'} />
          </dd>
        </dl>
      </div>

      <div className="card">
        <h2 className="card__title">Program name</h2>
        <form onSubmit={handleSubmit}>
          {saveError && <div className="banner banner--error" role="alert">{saveError}</div>}
          {saved && (
            <div className="banner banner--success" role="status">
              Settings saved.
            </div>
          )}
          <label className="field">
            <span className="field__label">Display name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </form>
      </div>
    </div>
  );
}
